import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { EditorContent, useEditor } from '@tiptap/react';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Loading from '../../../../components/loading';
import { useDocument } from '../hooks/useDocument';
import { useSaveDocument } from '../hooks/useSaveDocument';
import { usePatientDocumentData } from '../hooks/usePatientDocumentData';
import DocumentEditorToolbar from './documentEditorToolbar';
import InsertPatientDataMenu from './insertPatientDataMenu';
import { documentEditorExtensions, documentPageSx } from './documentEditorExtensions';

// contentJson may arrive as an object (jsonb) or as a serialized string; falls back to the HTML.
function parseContent(document) {
  if (document?.contentJson) {
    if (typeof document.contentJson === 'string') {
      try {
        return JSON.parse(document.contentJson);
      } catch (parseError) {
        console.error('ops! conteúdo do documento inválido' + parseError);
      }
    } else {
      return document.contentJson;
    }
  }
  return document?.contentHtml ?? '';
}

function toMessage(value, fallback) {
  if (typeof value === 'string' && value.trim()) return value;
  return fallback;
}

export default function DocumentEditorDialog({ open, patientId, documentId, onClose, onSaved }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const { fetchDocument, isLoading: isLoadingDocument } = useDocument();
  const { save, isSaving } = useSaveDocument();
  const {
    groups: patientDataGroups,
    isLoading: isLoadingPatientData,
    error: patientDataError,
  } = usePatientDocumentData(patientId, open);

  const [title, setTitle] = useState('');
  const [isDirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const [discardModal, setDiscardModal] = useState(false);

  const editor = useEditor({
    extensions: documentEditorExtensions,
    content: '',
    onUpdate: () => setDirty(true),
  });

  useEffect(() => {
    if (!open || !editor) return;

    setError('');
    setDirty(false);
    setDiscardModal(false);

    if (!documentId) {
      setTitle('');
      editor.commands.setContent('', { emitUpdate: false });
      editor.commands.focus('start');
      return;
    }

    fetchDocument(patientId, documentId)
      .then((returnFromApi) => {
        const document = returnFromApi.data ?? {};
        setTitle(document.title ?? '');
        editor.commands.setContent(parseContent(document), { emitUpdate: false });
      })
      .catch((returnFromApi) => {
        setError(toMessage(returnFromApi.response?.data, 'Erro ao carregar o documento.'));
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      });
  }, [open, documentId, patientId, editor, fetchDocument, navigate]);

  const requestClose = () => {
    if (isDirty) {
      setDiscardModal(true);
      return;
    }
    onClose();
  };

  const saveDocument = () => {
    setError('');
    if (!title.trim()) {
      setError('Informe o título do documento.');
      return;
    }
    if (editor.isEmpty) {
      setError('O documento está vazio.');
      return;
    }

    const document = {
      title: title.trim(),
      contentJson: editor.getJSON(),
      contentHtml: editor.getHTML(),
    };

    save(patientId, documentId, document)
      .then((returnFromApi) => {
        setDirty(false);
        onSaved(toMessage(returnFromApi.data, 'Documento salvo com sucesso.'));
      })
      .catch((returnFromApi) => {
        setError(toMessage(returnFromApi.response?.data, 'Erro ao salvar o documento.'));
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      });
  };

  const isBusy = isLoadingDocument || isSaving;

  return (
    <Dialog fullScreen open={open} onClose={requestClose}>
      <Loading isToHidden={isBusy === false} />

      <AppBar position="relative" color="default" elevation={1}>
        <Toolbar variant="dense" sx={{ gap: 1 }}>
          <IconButton edge="start" aria-label="fechar" onClick={requestClose}>
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 'bold' }} noWrap>
            {documentId ? 'Editar documento' : 'Novo documento'}
          </Typography>
          <Button variant="contained" onClick={saveDocument} disabled={isBusy || !editor}>
            Salvar
          </Button>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          backgroundColor: 'grey.200',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            backgroundColor: 'background.paper',
            px: 2,
            py: 1.5,
          }}
        >
          <TextField
            label="Título"
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
              setDirty(true);
            }}
            fullWidth
            required
            slotProps={{ htmlInput: { maxLength: 150 } }}
          />
        </Box>

        <Collapse in={!!error}>
          <Alert severity="error" onClose={() => setError('')} sx={{ borderRadius: 0 }}>
            {error}
          </Alert>
        </Collapse>

        {editor && (
          <DocumentEditorToolbar
            editor={editor}
            startActions={
              <InsertPatientDataMenu
                editor={editor}
                groups={patientDataGroups}
                isLoading={isLoadingPatientData}
                hasError={!!patientDataError}
              />
            }
          />
        )}

        <Box sx={{ py: isDesktop ? 3 : 1, px: isDesktop ? 2 : 0.5 }}>
          <Box
            onClick={() => editor?.commands.focus()}
            sx={{
              ...documentPageSx,
              backgroundColor: '#fff',
              width: '100%',
              maxWidth: '210mm',
              minHeight: isDesktop ? '297mm' : '70vh',
              mx: 'auto',
              p: isDesktop ? '20mm' : 2,
              boxShadow: 2,
              cursor: 'text',
            }}
          >
            <EditorContent editor={editor} />
          </Box>
        </Box>
      </Box>

      <Dialog open={discardModal} onClose={() => setDiscardModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Descartar alterações</DialogTitle>
        <DialogContent dividers>
          <Typography>
            As alterações feitas neste documento ainda não foram salvas e serão perdidas. Deseja
            continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscardModal(false)}>Continuar editando</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setDiscardModal(false);
              onClose();
            }}
          >
            Descartar
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

DocumentEditorDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

DocumentEditorDialog.defaultProps = {
  documentId: null,
};
